import type { Location } from '@/api/types';

/**
 * Which ways a guest can pay, and whether they should ever be asked to choose.
 *
 * Two things decide this and both have to agree: what the LOCATION has switched on, and what the DEVICE can
 * actually do. A restaurant with Apple Pay enabled still cannot offer it to a guest on an Android phone, and
 * showing a button that opens nothing is worse than not showing it.
 */

export type PaymentMethodId = 'card' | 'applePay' | 'googlePay';

/** What the browser in front of us can do, independent of what the restaurant has configured. */
export interface WalletCapabilities {
    applePay: boolean;
    googlePay: boolean;
}

declare global {
    interface Window {
        ApplePaySession?: { canMakePayments?: () => boolean };
    }
}

/**
 * Whether this browser can present an Apple Pay sheet.
 *
 * `ApplePaySession.canMakePayments()` rather than sniffing the user agent. It is the check Apple documents,
 * it is synchronous, and it is right in the places a UA string is wrong in both directions - Safari on macOS
 * can pay, and a Chrome-on-iOS user agent mentioning iPhone cannot.
 *
 * Deliberately not `canMakePaymentsWithActiveCard()`: that one is async and false for a guest who has the
 * hardware but no card provisioned yet. Apple's guidance is to show the button and let the sheet prompt them
 * to add one.
 */
export function canUseApplePay(): boolean {
    if (typeof window === 'undefined') return false;

    try {
        return window.ApplePaySession?.canMakePayments?.() === true;
    } catch {
        // canMakePayments throws in an insecure context or a cross-origin frame. Not available, not fatal.
        return false;
    }
}

/**
 * The methods to offer, in the order they should be shown.
 *
 * Card is always first and always present: it is the fallback that works everywhere, and every location
 * takes one.
 *
 * A wallet needs three things to be true - the location enabled it, the location has a merchant id for it,
 * and the device can present it. The merchant id matters as much as the flag: a location switched on but
 * never configured would otherwise offer a button that fails at the gateway, which a guest reads as the app
 * being broken rather than the restaurant being half set up.
 *
 * @param capabilities injectable so tests can describe a device rather than simulate one
 * @param cardOnly
 * The URL asked for card entry only, which is the handheld opening this app in a tablet WebView where both
 * wallets are inert. It wins over everything below: a location can have both enabled, configured and
 * presentable and still be shown card alone. See `payment/cardOnly.ts` for why it is not a UA check.
 */
export function availablePaymentMethods(
    location: Location,
    capabilities: WalletCapabilities,
    cardOnly = false,
): PaymentMethodId[] {
    const wallets = location.digitalWalletConfiguration;
    const methods: PaymentMethodId[] = ['card'];

    /*
     * Returning here rather than filtering afterwards, so `requiresMethodSelection` sees one method and the
     * tip step routes straight past the chooser. A selection screen offering a single option is a page whose
     * every visit ends in the same tap - and on the tablet it would be the only thing between the guest and
     * the card form.
     */
    if (cardOnly) return methods;

    if (wallets?.applePayEnabled && wallets.applePayMerchantId && capabilities.applePay) {
        methods.push('applePay');
    }

    if (wallets?.googlePayEnabled && wallets.googlePayMerchantId && capabilities.googlePay) {
        methods.push('googlePay');
    }

    return methods;
}

/**
 * Whether to show the guest a choice at all.
 *
 * One option is not a choice. If the only way to pay is by card - because the location enabled no wallets, or
 * because the one it enabled cannot run on this phone - the selection screen would be a page whose every
 * visit ends with the same tap. Skip it and go straight to the card form.
 */
export function requiresMethodSelection(methods: PaymentMethodId[]): boolean {
    return methods.length > 1;
}
